import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeliveryNotesList = ({ 
  notes, 
  selectedNotes, 
  onNoteSelect, 
  onSelectAll, 
  onRowClick,
  onCompleteDelivery,
  onPartialDelivery,
  isLoading 
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendiente', color: 'bg-warning-100 text-warning', icon: 'Clock' },
      in_progress: { label: 'En progreso', color: 'bg-primary-100 text-primary', icon: 'Truck' },
      completed: { label: 'Completado', color: 'bg-success-100 text-success', icon: 'CheckCircle' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-body-medium ${config.color}`}>
        <Icon name={config.icon} size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-8">
        <div className="text-center">
          <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-text-muted">Cargando albaranes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading-semibold text-text-primary">
            Albaranes de Entrega
          </h2>
          <div className="text-sm text-text-muted">
            {notes.length} {notes.length === 1 ? 'albarán' : 'albaranes'}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedNotes.length === notes.length && notes.length > 0}
                  onChange={onSelectAll}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Albarán
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Entrega
              </th>
              <th className="px-4 py-3 text-right text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {notes.map((note) => (
              <tr 
                key={note.id}
                className={`hover:bg-secondary-50 transition-micro cursor-pointer ${
                  selectedNotes.includes(note.id) ? 'bg-primary-50' : ''
                }`}
                onClick={() => onRowClick?.(note)}
              >
                <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedNotes.includes(note.id)}
                    onChange={() => onNoteSelect(note.id)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                  />
                </td>
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-body-medium text-text-primary">
                      {note.id}
                    </div>
                    <div className="text-xs text-text-muted">
                      Pedido: {note.orderId}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-body-medium text-text-primary">
                      {note.customerName}
                    </div>
                    <div className="text-xs text-text-muted">
                      {note.products?.length} {note.products?.length === 1 ? 'producto' : 'productos'}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="space-y-1">
                    {getStatusBadge(note.status)}
                    {note.isPartialDelivery && (
                      <div className="text-xs text-warning">
                        Entrega parcial
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-text-secondary">
                    {formatDate(note.deliveryDate)}
                  </div>
                  {note.deliveryTime && (
                    <div className="text-xs text-text-muted">
                      {new Date(note.deliveryTime).toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end space-x-2">
                    {note.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCompleteDelivery(note.id, {})}
                        className="text-xs"
                      >
                        Completar
                      </Button>
                    )}
                    
                    <div className="relative group">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="MoreVertical"
                        iconSize={16}
                      />
                      <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-lg shadow-modal border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-micro z-dropdown">
                        <div className="py-2">
                          <button
                            onClick={() => window.print()}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                          >
                            <Icon name="Printer" size={16} />
                            <span>Imprimir</span>
                          </button>
                          <button
                            onClick={() => console.log('Download PDF', note.id)}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                          >
                            <Icon name="Download" size={16} />
                            <span>Descargar PDF</span>
                          </button>
                          {note.status === 'pending' && (
                            <button
                              onClick={() => onPartialDelivery(note.id, {}, [])}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                            >
                              <Icon name="Package" size={16} />
                              <span>Entrega parcial</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {notes.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="FileText" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-body-medium text-text-primary mb-2">
            No hay albaranes
          </h3>
          <p className="text-text-muted">
            No se encontraron albaranes con los filtros aplicados
          </p>
        </div>
      )}
    </div>
  );
};

export default DeliveryNotesList;